import { SkeletonElement } from '@notino/react-styleguide'
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'

// types
import { SalonEmployeesResponse } from '../../../../types/types'

// utils
import { AppContext } from '../../../../utils/appProvider'
import { SALON_EMPLOYEES_LIMIT } from '../../../../utils/enums'

// components
import ErrorScreen from '../../../../atoms/ErrorScreen/ErrorScreen'
import Empty from '../../../../atoms/Empty/Empty'
import ElementsSlider from '../../../../components/ElementsSlider/ElementsSlider'

// styles
import * as SC from './SalonEmployeesStyles'

type SalonEmployeesProps = {
	salonID: string
	employeesData: SalonEmployeesResponse | undefined
}

const LOADING_SKELETONS_COUNT = 5

const SalonEmployees = (props: SalonEmployeesProps) => {
	const { salonID, employeesData } = props
	const { apiBrowser } = useContext(AppContext)

	const [employees, setEmployees] = useState<SalonEmployeesResponse | undefined>(employeesData)
	const [isLoading, setIsLoading] = useState(false)
	const [isLoadingMore, setIsLoadingMore] = useState(false)

	const elementsSliderRef = useRef<{ scrollElementRef: HTMLDivElement | null }>(null)
	// const scrollableContent = useRef<HTMLDivElement>(null)
	const skeletonRef = useRef<HTMLDivElement>(null)

	const fetchEmployees = useCallback(
		async (data: { page: number }) => {
			if (data.page === 1) {
				setIsLoading(true)
			} else {
				setIsLoadingMore(true)
			}

			try {
				const response = await apiBrowser.b2c.getSalonEmployees(salonID, { page: data.page, limit: SALON_EMPLOYEES_LIMIT })

				setEmployees((prev) => ({
					employees: [...(prev?.employees ?? []), ...response.employees],
					pagination: response.pagination
				}))
			} catch (error) {
				setEmployees(undefined)
				// eslint-disable-next-line no-console
				console.error('Error fetching employees:', error)
			} finally {
				if (data.page === 1) {
					setIsLoading(false)
				} else {
					setIsLoadingMore(false)
				}
			}
		},
		[apiBrowser.b2c, salonID]
	)

	// Intersection Observer for lazy loading
	useEffect(() => {
		if (!elementsSliderRef.current?.scrollElementRef || !skeletonRef.current || isLoadingMore || isLoading) {
			return undefined
		}

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting) {
					// Load next page if available
					if (employees && employees.pagination.page < employees.pagination.totalPages && !isLoadingMore && !isLoading) {
						fetchEmployees({ page: employees.pagination.page + 1 })
					}
				}
			},
			{
				root: elementsSliderRef.current.scrollElementRef,
				threshold: 0.5 // Trigger when 50% visible
			}
		)

		observer.observe(skeletonRef.current)

		return () => {
			observer.disconnect()
		}
	}, [employees?.pagination.page, employees?.pagination.totalPages, isLoadingMore, employees, isLoading, fetchEmployees])

	const getContent = () => {
		if (isLoading) {
			return (
				<ElementsSlider ref={elementsSliderRef}>
					{Array.from({ length: LOADING_SKELETONS_COUNT }).map((_, index) => (
						<SC.EmployeeContainer key={index}>
							<SC.SkeletonAvatarElement width={'80px'} height={'80px'} />
							<SkeletonElement width={'100px'} height={'1.25rem'} />
						</SC.EmployeeContainer>
					))}
				</ElementsSlider>
			)
		}

		if (!employees) {
			return <ErrorScreen icon={null} minHeight={'164px'} button={{ onClick: () => fetchEmployees({ page: 1 }) }} />
		}

		if (employees?.employees.length === 0) {
			return <Empty icon={null} minHeight={'164px'} />
		}

		return (
			<ElementsSlider ref={elementsSliderRef} fading={false}>
				{employees.employees.map((employee) => (
					<SC.EmployeeContainer key={employee.id}>
						<SC.EmployeeImageContainer>
							<img alt={employee.firstName} loading={'lazy'} src={employee.image.resizedImages.thumbnail} width={80} height={80} />
						</SC.EmployeeImageContainer>
						<SC.EmployeeName>{employee.firstName}</SC.EmployeeName>
					</SC.EmployeeContainer>
				))}
				{employees.pagination.page < employees.pagination.totalPages && (
					<SC.EmployeeContainer ref={skeletonRef}>
						<SC.SkeletonAvatarElement width={'80px'} height={'80px'} />
						<SkeletonElement width={'100px'} height={'1.25rem'} />
					</SC.EmployeeContainer>
				)}
			</ElementsSlider>
		)
	}

	return <SC.EmployeesContainer>{getContent()}</SC.EmployeesContainer>
}

export default SalonEmployees
